import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridToolbarContainer, GridToolbarQuickFilter, itIT } from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import { MdBusiness, MdDescription } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../api/api";
import { IipayFasciaDetails } from "../../components/IipayFasciaDetails";
import Modal from "../../components/Modal";
import { knowAcces } from "../../utils/utils";
import { EXPORT_BUTTON_CLASS } from "../../utils/exportButtonStyle";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const euro = (value) => `${formatItalianNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00a0€`;

const TIPI = [
  ["Z", "Zucchetti", "bg-blue-100 text-blue-700 border-blue-200"],
  ["I", "iiPay", "bg-green-100 text-green-700 border-green-200"],
  ["B", "BPoint", "bg-purple-100 text-purple-700 border-purple-200"],
];

const Metric = ({ icon, label, value, tone, onClick, active, detail }) => (
  <div role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
    onClick={onClick} onKeyDown={(event) => { if (onClick && (event.key === "Enter" || event.key === " ")) onClick(); }}
    className={`rounded-xl border p-4 shadow-sm ${tone} ${onClick ? "cursor-pointer transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500" : ""} ${active ? "ring-2 ring-emerald-500" : ""}`}>
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
        <div className="mt-1 text-2xl font-bold">{value}</div>
        {detail && <div className="mt-2 border-t border-current/60 pt-2 text-xs font-semibold opacity-80">{detail}</div>}
      </div>
      {icon && <div className="rounded-full bg-white/70 p-3 text-2xl shadow-sm">{icon}</div>}
    </div>
  </div>
);

export const SezioneUno = ({ data }) => {
  const [anno, mese] = data.split("-").map(Number);
  const [periodo, setPeriodo] = useState(null);
  const [rows, setRows] = useState([]);
  const [showZero, setShowZero] = useState(false);
  const [clientiPayroll, setClientiPayroll] = useState([]);
  const [tipologieFiltro, setTipologieFiltro] = useState(() => new Set());
  const [totaliModalOpen, setTotaliModalOpen] = useState(false);
  const [bpointDetail, setBpointDetail] = useState(null);
  const [bpointDetailLoading, setBpointDetailLoading] = useState(false);
  const [bpointSummaryDetail, setBpointSummaryDetail] = useState(null);
  const [tipologieTotaliModal, setTipologieTotaliModal] = useState(new Set());
  const [soloSenzaFatturazione, setSoloSenzaFatturazione] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isAdmin = knowAcces(["parc_admin"]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const period = await api.get(`/billing/periods/${anno}/${mese}`, { signal });
      setPeriodo(period.data);
      const [lines, riepilogo] = await Promise.all([
        api.get(`/billing/lines/${period.data.id}`, {
          params: { sezione: "payroll", include_zero: "1" }, signal,
        }),
        api.get("/billing/cedolini-riepilogo", { params: { anno, mese }, signal }),
      ]);
      setRows(lines.data || []);
      setClientiPayroll((riepilogo.data?.rows || []).filter((row) => ["Z", "I", "B"].includes(row.tipologia_fatturazione)));
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      toast.error(error.response?.data?.message || "Errore caricamento sezione 1");
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [anno, mese, isAdmin]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const rowsImporto = useMemo(() => showZero ? rows : rows.filter((row) => Number(row.totale) !== 0), [rows, showZero]);
  const rowsTipologia = useMemo(() => tipologieFiltro.size === 0 ? rowsImporto : rowsImporto.filter((row) => tipologieFiltro.has(row.tipologia_fatturazione)), [rowsImporto, tipologieFiltro]);
  const clientiTipologia = useMemo(() => tipologieFiltro.size === 0 ? clientiPayroll : clientiPayroll.filter((row) => tipologieFiltro.has(row.tipologia_fatturazione)), [clientiPayroll, tipologieFiltro]);
  const active = useMemo(() => rowsTipologia.filter((r) => !["esclusa", "eliminata"].includes(r.stato)), [rowsTipologia]);
  const total = useMemo(() => active.reduce((sum, r) => sum + Number(r.totale || 0), 0), [active]);
  const totaliPerTipologia = useMemo(() => active.reduce((result, row) => {
    const tipo = row.tipologia_fatturazione;
    if (["Z", "I", "B"].includes(tipo)) result[tipo] += Number(row.totale || 0);
    return result;
  }, { Z: 0, I: 0, B: 0 }), [active]);
  const clientiPerTipologia = useMemo(() => clientiTipologia.reduce((result, row) => {
    const tipo = row.tipologia_fatturazione;
    if (["Z", "I", "B"].includes(tipo)) result[tipo] += 1;
    return result;
  }, { Z: 0, I: 0, B: 0 }), [clientiTipologia]);
  const totaliPerPrestazione = useMemo(() => {
    const result = new Map();
    rowsImporto.filter((row) => !["esclusa", "eliminata"].includes(row.stato)
      && (tipologieTotaliModal.size === 0 || tipologieTotaliModal.has(row.tipologia_fatturazione))).forEach((row) => {
      const codice = String(row.nuovo_codice_ago || row.prestazione || "—").replace(/^0+(?=\d)/, "");
      const current = result.get(codice) || {
        codice,
        descrizione: row.descrizione_prestazione || row.descrizione_fattura || "Prestazione payroll",
        quantita: 0,
        totale: 0,
        tipologie: new Set(),
      };
      current.quantita += Number(row.quantita || 0);
      current.totale += Number(row.totale || 0);
      current.tipologie.add(row.tipologia_fatturazione);
      result.set(codice, current);
    });
    return [...result.values()].sort((a, b) => a.codice.localeCompare(b.codice, "it", { numeric: true }));
  }, [rowsImporto, tipologieTotaliModal]);
  const totalePrestazioniModal = useMemo(() => totaliPerPrestazione.reduce((sum, item) => sum + item.totale, 0), [totaliPerPrestazione]);
  const codiciFatturati = useMemo(() => new Set(active
    .filter((row) => Number(row.totale || 0) !== 0)
    .map((row) => row.codice_univoco_azienda)), [active]);
  const clientiSenzaFatturazione = useMemo(() => clientiTipologia.filter(
    (row) => !codiciFatturati.has(row.codice_univoco_azienda)
  ), [clientiTipologia, codiciFatturati]);
  const codiciSenzaFatturazione = useMemo(() => new Set(
    clientiSenzaFatturazione.map((row) => row.codice_univoco_azienda)
  ), [clientiSenzaFatturazione]);
  const righeConClientiSenzaFatturazione = useMemo(() => {
    const codiciPresenti = new Set(rowsTipologia.map((row) => row.codice_univoco_azienda));
    const placeholder = clientiSenzaFatturazione
      .filter((row) => !codiciPresenti.has(row.codice_univoco_azienda))
      .map((row) => ({
        ...row,
        id: `senza-fatturazione-${row.codice_univoco_azienda}`,
        tipo_listino: "—",
        prestazione: "—",
        descrizione_prestazione: "Nessuna fatturazione nel mese",
        quantita: 0,
        importo_unitario: 0,
        totale: 0,
        stato: "bozza",
      }));
    return [...rowsTipologia, ...placeholder];
  }, [rowsTipologia, clientiSenzaFatturazione]);
  const righeVisibili = useMemo(() => soloSenzaFatturazione
    ? righeConClientiSenzaFatturazione.filter((row) => codiciSenzaFatturazione.has(row.codice_univoco_azienda))
    : showZero ? rowsTipologia : rowsTipologia.filter((row) => codiciFatturati.has(row.codice_univoco_azienda)),
  [soloSenzaFatturazione, righeConClientiSenzaFatturazione, codiciSenzaFatturazione, codiciFatturati, rowsTipologia, showZero]);
  const clients = clientiTipologia.length;

  const exportExcel = async () => {
    if (!periodo?.id) return;
    setExporting(true);
    try {
      const tipi = [...tipologieFiltro];
      const sezioneExport = tipi.length === 1
        ? (tipi[0] === "Z" ? "payroll_zucchetti" : tipi[0] === "I" ? "payroll_iipay" : "payroll_bpoint")
        : "payroll";
      const response = await api.get(`/billing/lines/${periodo.id}/export`, {
        params: { sezione: sezioneExport, include_zero: showZero ? "1" : "0" }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url;
      link.download = `payroll_${tipi.length ? tipi.join("").toLowerCase() : "tutti"}_${data}.xlsx`; link.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore esportazione sezione 1"); }
    finally { setExporting(false); }
  };

  const openBpointDetail = async (row) => {
    if (row.tipologia_fatturazione !== 'B' || !row.id || String(row.id).startsWith('senza-')) return;
    setBpointDetailLoading(true);
    try {
      const response = await api.get(`/billing/bpoint-line/${row.id}/details`);
      setBpointDetail(response.data);
    } catch (error) { toast.error(error.response?.data?.message || 'Errore caricamento dettaglio BPoint'); }
    finally { setBpointDetailLoading(false); }
  };

  const openBpointSummaryDetail = async (item) => {
    if (!periodo?.id || !item.tipologie.has('B') || ![427, 442, 446, 447, 456, 461, 462].includes(Number(item.codice))) return;
    try {
      const response = await api.get('/billing/bpoint-summary-details', { params: { period_id: periodo.id, prestazione: item.codice } });
      setBpointSummaryDetail(response.data);
    } catch (error) { toast.error(error.response?.data?.message || 'Errore caricamento spaccato mensile BPoint'); }
  };

  const columns = [
    { field: "ragione_sociale", headerName: "Cliente", flex: 1, minWidth: 250 },
    { field: "referente_payroll1", headerName: "Referente payroll 1", width: 175 },
    { field: "tipo_listino", headerName: "Tipo listino", width: 110 },
    { field: "prestazione", headerName: "Cod. prestazione", width: 135,
      valueGetter: ({ row }) => String(row.nuovo_codice_ago || row.prestazione).replace(/^0+(?=\d)/, ""),
      sortComparator: (a, b) => String(a).localeCompare(String(b), "it", { numeric: true, sensitivity: "base" }),
      renderCell: ({ row, value }) => <div className="flex items-center gap-1.5">
        <span>{value}</span>
        {String(row.prestazione) === "186" && row.tipo_listino === "Forfait" && row.forfait_fino_a && (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700">
            fino a {row.forfait_fino_a}
          </span>
        )}
      </div> },
    { field: "descrizione_prestazione", headerName: "Descrizione", flex: 1.2, minWidth: 300,
      valueGetter: ({ row }) => row.descrizione_payroll || row.descrizione_prestazione || row.descrizione_fattura || "—" },
    { field: "fascia_iipay", headerName: "Fascia iiPay", width: 300, align: "center", headerAlign: "center", sortable: false, filterable: false,
      renderCell: ({ row }) => <IipayFasciaDetails row={row} /> },
    { field: "quantita", headerName: "Quantità", type: "number", width: 115, align: "center", headerAlign: "center",
      renderCell: ({ value }) => value ?? "1.00" },
    { field: "importo_unitario", headerName: "Valore unitario", type: "number", width: 135, align: "center", headerAlign: "center",
      valueFormatter: ({ value }) => euro(value) },
    { field: "totale", headerName: "Totale", type: "number", width: 130, align: "center", headerAlign: "center",
      renderCell: ({ value }) => <strong>{euro(value)}</strong> },
  ];

  const Toolbar = () => (
    <GridToolbarContainer className="flex justify-between gap-3 border-b px-2 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <GridToolbarQuickFilter placeholder="Cerca cliente, codice o prestazione..." />
        <div className="flex items-center gap-1">
          {[
            ["Z", "Zucchetti", "bg-blue-100 text-blue-700 border-blue-300", "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"],
            ["I", "iiPay", "bg-green-100 text-green-700 border-green-300", "bg-white text-green-700 border-green-200 hover:bg-green-50"],
            ["B", "Bpoint", "bg-purple-100 text-purple-700 border-purple-300", "bg-white text-purple-700 border-purple-200 hover:bg-purple-50"],
          ].map(([id, title, activeClass, idleClass]) => (
            <button key={id} type="button" title={title}
              onClick={() => setTipologieFiltro((prev) => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              })}
              className={`rounded border px-2.5 py-0.5 text-xs font-semibold transition-colors ${tipologieFiltro.has(id) ? activeClass : idleClass}`}>
              {id}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" aria-pressed={showZero} onClick={() => setShowZero((value) => !value)}
          className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${!showZero ? "bg-orange-100 text-orange-700 border-orange-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}>
          {showZero ? "Nascondi zero" : "Mostra zero"}
        </button>
        <button onClick={exportExcel} disabled={exporting || !rows.length} className={EXPORT_BUTTON_CLASS}>
          <MdDescription className="mr-1 inline" />{exporting ? "Esportazione..." : "Excel"}
        </button>
      </div>
    </GridToolbarContainer>
  );

  return (
    <div className="el-container space-y-4 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Metric label="Totale sezione" value={euro(total)} onClick={() => {
          setTipologieTotaliModal(new Set());
          setTotaliModalOpen(true);
        }}
          detail={<span className="flex flex-wrap gap-x-4 gap-y-1"><span>Zucchetti: {euro(totaliPerTipologia.Z)}</span><span>iiPay: {euro(totaliPerTipologia.I)}</span><span>BPoint: {euro(totaliPerTipologia.B)}</span></span>}
          tone="border-blue-200 bg-blue-50 text-blue-800" />
        <Metric icon={<MdBusiness />} label="Clienti" value={clients}
          detail={<span className="flex flex-wrap gap-x-4 gap-y-1"><span>Zucchetti: {clientiPerTipologia.Z}</span><span>iiPay: {clientiPerTipologia.I}</span><span>BPoint: {clientiPerTipologia.B}</span><span>Senza fatturazione: {clientiSenzaFatturazione.length}</span></span>}
          onClick={() => setSoloSenzaFatturazione((value) => !value)} active={soloSenzaFatturazione}
          tone="border-emerald-200 bg-emerald-50 text-emerald-800" />
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        style={{ height: "calc(100vh - 140px)", minHeight: 620 }}>
        <DataGridPro rows={righeVisibili} columns={columns} getRowId={(row) => row.id} loading={loading}
          onRowClick={({ row }) => openBpointDetail(row)}
          getRowClassName={({ row }) => row.tipologia_fatturazione === 'B' ? 'cursor-pointer hover:bg-purple-50' : ''}
          density="compact" disableRowSelectionOnClick localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          slots={{ toolbar: Toolbar, loadingOverlay: LinearProgress }} />
      </div>
      {totaliModalOpen && (
        <Modal isOpen size="compact" closeButton={() => setTotaliModalOpen(false)}
          headerText={<div className="text-center"><div>Totali per codice prestazione</div><div className="mt-0.5 text-xs font-normal text-gray-500">Payroll {String(mese).padStart(2, "0")}/{anno}</div></div>}>
          <div className="overflow-hidden rounded-md border border-slate-200">
            <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Mostra tipologia</div>
                <div className="flex gap-2">
                {TIPI.map(([id, label, colors]) => (
                  <button key={id} type="button" onClick={() => setTipologieTotaliModal((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
                    title={label}
                    className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologieTotaliModal.has(id) ? `${colors} ring-1 ring-current` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>
                    {id}
                  </button>
                ))}
                </div>
              </div>
              <div className="rounded-xl border border-blue-100 bg-white px-5 py-3 text-right shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Totale selezionato</div>
                <div className="mt-0.5 text-2xl font-bold text-blue-700">{euro(totalePrestazioniModal)}</div>
              </div>
            </div>
            <div className="max-h-[58vh] overflow-auto p-4">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500">
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left">Codice</th>
                    <th className="px-3 py-2 text-left">Descrizione</th>
                    <th className="px-3 py-2 text-right">Quantità</th>
                    <th className="px-3 py-2 text-right">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {totaliPerPrestazione.map((item) => {
                    const isZ = item.tipologie.has("Z");
                    const isI = item.tipologie.has("I");
                    const isB = item.tipologie.has("B");
                    const tipiPresenti = [isZ, isI, isB].filter(Boolean).length;
                    const rowClass = tipiPresenti > 1
                      ? "border-l-blue-500 bg-gradient-to-r from-blue-50/90 to-emerald-50/90 hover:from-blue-100/80 hover:to-emerald-100/80"
                      : isB
                        ? "border-l-purple-500 bg-purple-50/70 hover:bg-purple-100/70"
                      : isI
                        ? "border-l-emerald-500 bg-emerald-50/70 hover:bg-emerald-100/70"
                        : "border-l-blue-500 bg-blue-50/70 hover:bg-blue-100/70";
                    return (
                    <tr key={item.codice} onClick={() => openBpointSummaryDetail(item)}
                      className={`border-b border-l-4 border-gray-100 transition ${rowClass} ${isB && [427,442,446,447,456,461,462].includes(Number(item.codice)) ? 'cursor-pointer' : ''}`}>
                      <td className={`px-3 py-2 font-semibold ${isB && tipiPresenti === 1 ? "text-purple-700" : isI && tipiPresenti === 1 ? "text-emerald-700" : "text-blue-700"}`}>{item.codice}</td>
                      <td className="px-3 py-2 text-gray-700">{item.descrizione}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatItalianNumber(item.quantita, { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{euro(item.totale)}</td>
                    </tr>
                    );
                  })}
                  {!totaliPerPrestazione.length && <tr><td colSpan="3" className="px-3 py-8 text-center text-gray-400">Nessuna prestazione nel periodo</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}
      {(bpointDetail || bpointDetailLoading) && (
        <Modal isOpen size="compact" closeButton={() => setBpointDetail(null)}
          headerText={bpointDetailLoading ? 'Dettaglio calcolo BPoint' : `Dettaglio BPoint ${String(bpointDetail.line.prestazione).replace(/^0+/, '')}`}>
          {bpointDetailLoading ? <div className="p-8 text-center text-gray-500">Caricamento...</div> : (() => {
            const detail = bpointDetail;
            const code = Number(detail.line.prestazione);
            const isCovip = code === 448;
            const valueField = code === 442 ? 'edili' : code === 446 ? 'dirigenti' : 'ordinari';
            const label = code === 442 ? 'Cedolini edili' : code === 446 ? 'Cedolini dirigenti' : 'Cedolini ordinari';
            return <div className="space-y-4 p-1">
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                <div className="font-semibold text-purple-950">{detail.line.ragione_sociale}</div>
                <div className="mt-1 text-sm text-purple-700">{detail.line.descrizione_extra_fattura}</div>
              </div>
              {isCovip ? <table className="w-full text-sm"><thead className="bg-purple-50"><tr><th className="p-2 text-left">Competenza</th><th className="p-2 text-right">Fondi</th><th className="p-2 text-right">Iscritti</th><th className="p-2 text-right">Importo mensile</th></tr></thead>
                <tbody>{(detail.covipMonths || []).map((month) => <tr key={`${month.anno}-${month.mese}`} className="border-t"><td className="p-2">{String(month.mese).padStart(2, '0')}/{month.anno}</td><td className="p-2 text-right font-semibold">{formatItalianNumber(month.funds || 0)}</td><td className="p-2 text-right font-semibold">{formatItalianNumber(month.employees || 0)}</td><td className="p-2 text-right font-semibold text-purple-800">{euro(month.total)}</td></tr>)}</tbody>
                <tfoot className="border-t-2 bg-gray-50"><tr><td className="p-2 font-semibold" colSpan="3">Totale trimestre</td><td className="p-2 text-right font-bold text-purple-800">{euro((detail.covipMonths || []).reduce((sum, month) => sum + Number(month.total || 0), 0))}</td></tr></tfoot>
              </table> : <table className="w-full text-sm"><thead className="bg-gray-50"><tr><th className="p-2 text-left">Competenza</th><th className="p-2 text-right">{label}</th></tr></thead>
                <tbody>{detail.months.map((month) => <tr key={`${month.anno}-${month.mese}`} className="border-t"><td className="p-2">{String(month.mese).padStart(2, '0')}/{month.anno}</td><td className="p-2 text-right font-semibold">{formatItalianNumber(month[valueField] || 0)}</td></tr>)}</tbody>
              </table>}
              {[427, 447, 461, 462].includes(code) && <div className="grid grid-cols-2 gap-3 text-sm"><div className="rounded border p-3"><div className="text-gray-500">Media trimestrale</div><div className="text-lg font-bold">{detail.average}</div></div><div className="rounded border p-3"><div className="text-gray-500">{[447,461].includes(code) ? 'Mesi attivi' : 'Mensilità aggiuntive'}</div><div className="text-lg font-bold">{[447,461].includes(code) ? detail.months.filter((row) => Number(row.ordinari) > 0).length : detail.additional}</div></div></div>}
              <div className="rounded-lg border bg-gray-50 p-3 text-sm"><div className="font-semibold">Formula applicata</div><div className="mt-1 text-gray-700">{detail.calculation}</div></div>
              <div className="grid grid-cols-3 gap-3 text-sm"><div><span className="text-gray-500">Quantità</span><div className="font-bold">{formatItalianNumber(detail.line.quantita)}</div></div><div><span className="text-gray-500">Valore unitario</span><div className="font-bold">{euro(detail.line.importo_unitario)}</div></div><div><span className="text-gray-500">Totale</span><div className="font-bold text-purple-800">{euro(detail.line.totale)}</div></div></div>
            </div>;
          })()}
        </Modal>
      )}
      {bpointSummaryDetail && (
        <Modal isOpen size="compact" closeButton={() => setBpointSummaryDetail(null)} headerText={`Spaccato mensile BPoint ${bpointSummaryDetail.prestazione}`}>
          {(() => {
            const code = Number(bpointSummaryDetail.prestazione);
            const field = code === 442 ? 'edili' : code === 446 ? 'dirigenti' : 'ordinari';
            const label = code === 442 ? 'Cedolini edili' : code === 446 ? 'Cedolini dirigenti' : 'Cedolini ordinari';
            return <div className="space-y-4">
              <table className="w-full text-sm"><thead className="bg-purple-50"><tr><th className="p-2 text-left">Mese</th><th className="p-2 text-right">{label}</th></tr></thead><tbody>
                {bpointSummaryDetail.months.map((month) => <tr key={`${month.anno}-${month.mese}`} className="border-t"><td className="p-2">{String(month.mese).padStart(2, '0')}/{month.anno}</td><td className="p-2 text-right font-semibold">{formatItalianNumber(month[field] || 0)}</td></tr>)}
              </tbody></table>
              {[427, 462].includes(code) && <div className="rounded border border-purple-200 bg-purple-50 p-3"><span className="text-sm text-purple-700">Mensilità aggiuntive:</span> <strong>{formatItalianNumber(bpointSummaryDetail.additional)}</strong></div>}
              <div className="grid grid-cols-3 gap-3 rounded border bg-gray-50 p-3 text-sm"><div><div className="text-gray-500">Righe</div><strong>{bpointSummaryDetail.totals.righe}</strong></div><div><div className="text-gray-500">Quantità fatturata</div><strong>{formatItalianNumber(bpointSummaryDetail.totals.quantita)}</strong></div><div><div className="text-gray-500">Totale</div><strong className="text-purple-800">{euro(bpointSummaryDetail.totals.totale)}</strong></div></div>
            </div>;
          })()}
        </Modal>
      )}
    </div>
  );
};
